/**
 * VideoTiebreakDialog — chấm điểm tie-break Video clip (Điều 10.3).
 *
 * Audit 2026-05-21: extract khỏi TeamsPage để reuse ở IntroVideoPage (F.1).
 * Video tiebreak chỉ là tiêu chí phụ khi 2+ đội đồng điểm R1 — KHÔNG cộng vào
 * điểm chính R1/200.
 */
import { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import { Movie, Save } from '@mui/icons-material'
import { useSetVideoTiebreakMutation } from '../store/api/reportsApi'

export interface VideoTiebreakDialogTeam {
  id: string
  name: string
  intro_video?: any
  video_tiebreak?: { creativity?: number; presentation?: number; professionalism?: number }
}

interface Props {
  team: VideoTiebreakDialogTeam | null
  onClose: () => void
  onSaved?: () => void
}

export default function VideoTiebreakDialog({ team, onClose, onSaved }: Props) {
  const [creativity, setCreativity] = useState<number>(0)
  const [presentation, setPresentation] = useState<number>(0)
  const [professionalism, setProfessionalism] = useState<number>(0)
  const [err, setErr] = useState<string | null>(null)
  const [setTiebreak, { isLoading }] = useSetVideoTiebreakMutation()

  useEffect(() => {
    if (team) {
      const tb = team.video_tiebreak || {}
      setCreativity(Number(tb.creativity || 0))
      setPresentation(Number(tb.presentation || 0))
      setProfessionalism(Number(tb.professionalism || 0))
      setErr(null)
    }
  }, [team])

  if (!team) return null

  const introVideo = team.intro_video
  const total = creativity + presentation + professionalism

  const handleSave = async () => {
    setErr(null)
    if ([creativity, presentation, professionalism].some((v) => v < 0 || v > 10)) {
      setErr('Mỗi tiêu chí phải trong khoảng 0–10.')
      return
    }
    try {
      await setTiebreak({
        team_id: team.id,
        creativity,
        presentation,
        professionalism,
      }).unwrap()
      onSaved?.()
      onClose()
    } catch (e: any) {
      setErr(e?.data?.detail || e?.message || 'Lưu thất bại')
    }
  }

  const Score = ({
    label,
    value,
    onChange,
    desc,
  }: {
    label: string
    value: number
    onChange: (v: number) => void
    desc: string
  }) => (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" mb={1}>
        {desc}
      </Typography>
      <TextField
        type="number"
        size="small"
        fullWidth
        value={value}
        onChange={(e) => onChange(Math.max(0, Math.min(10, Number(e.target.value) || 0)))}
        inputProps={{ min: 0, max: 10, step: 0.5 }}
        helperText="Thang điểm 0–10"
      />
    </Paper>
  )

  return (
    <Dialog open={Boolean(team)} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Movie color="warning" />
          <span>Tie-break Video clip — {team.name}</span>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <b>Điều 10.3 Thể lệ:</b> Khi 2+ đội có tổng điểm Vòng Sơ loại bằng nhau, Ban Giám khảo
            xét tiêu chí phụ qua Video clip giới thiệu — mỗi tiêu chí 0–10đ. Điểm này KHÔNG cộng
            vào tổng điểm chính R1/200.
          </Typography>
        </Alert>

        {introVideo?.storage_path || introVideo?.file_name ? (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              📹 Video giới thiệu của đội
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              {introVideo.file_name}
            </Typography>
          </Paper>
        ) : (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Đội chưa upload video giới thiệu — tie-break có thể không áp dụng được.
          </Alert>
        )}

        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' } }}>
          <Score
            label="1. Tính sáng tạo"
            value={creativity}
            onChange={setCreativity}
            desc="Cách thức thể hiện nội dung, ý tưởng, dấu ấn riêng"
          />
          <Score
            label="2. Kỹ năng trình bày"
            value={presentation}
            onChange={setPresentation}
            desc="Khả năng diễn đạt, ngôn ngữ, hình ảnh, phương tiện"
          />
          <Score
            label="3. Tính chuyên nghiệp"
            value={professionalism}
            onChange={setProfessionalism}
            desc="Sự chỉn chu, cấu trúc video, mức độ đầu tư chất lượng"
          />
        </Box>

        <Paper sx={{ mt: 2, p: 2, bgcolor: 'background.default', textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Tổng điểm tie-break (chỉ dùng phân thứ hạng khi đồng điểm)
          </Typography>
          <Typography variant="h4" fontWeight={700} color="warning.main">
            {total.toFixed(1)} / 30
          </Typography>
        </Paper>

        {err && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {err}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Đóng</Button>
        <Button variant="contained" onClick={handleSave} disabled={isLoading} startIcon={<Save />}>
          {isLoading ? 'Đang lưu…' : 'Lưu điểm'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
